import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  Grid,
  Stack
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Save as SaveIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from '../utils/axios';
import { Link as RouterLink } from 'react-router-dom';

const OCIASettings = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addRecipientDialog, setAddRecipientDialog] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [newYear, setNewYear] = useState('');

  const deepCompare = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const hasChanges = !deepCompare(settings, originalSettings);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/ocia/settings');
      setSettings(response.data);
      setOriginalSettings(JSON.parse(JSON.stringify(response.data)));
    } catch (err) {
      setError('Error fetching settings');
      console.error('Error fetching OCIA settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    let current = obj;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        if (!current[key] || typeof current[key] !== 'object') current[key] = {};
        current = current[key];
      }
    });
  };

  const handleSettingsChange = (field, value) => {
    const updated = JSON.parse(JSON.stringify(settings));
    setNestedValue(updated, field, value);
    setSettings(updated);
    setSuccess(null);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) {
      setSuccess('No changes to save.');
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.put('/ocia/settings', settings);
      setSettings(response.data);
      setOriginalSettings(JSON.parse(JSON.stringify(response.data)));
      setSuccess('Settings saved successfully!');
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving OCIA settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
    setError(null);
    setSuccess(null);
  };

  const handleAddRecipient = () => {
    const updated = JSON.parse(JSON.stringify(settings));
    updated.emailNotifications.newRegistration.recipients = [
      ...(updated.emailNotifications.newRegistration.recipients || []),
      newRecipient
    ];
    setSettings(updated);
    setAddRecipientDialog(false);
    setNewRecipient('');
  };

  const handleRemoveRecipient = (emailToRemove) => {
    const updated = JSON.parse(JSON.stringify(settings));
    updated.emailNotifications.newRegistration.recipients =
      (updated.emailNotifications.newRegistration.recipients || []).filter(r => r !== emailToRemove);
    setSettings(updated);
  };

  const handleAddYear = () => {
    const year = newYear.trim();
    if (!year) return;
    const updated = JSON.parse(JSON.stringify(settings));
    const years = updated.availableSchoolYears || [];
    if (!years.includes(year)) {
      updated.availableSchoolYears = [...years, year].sort();
    }
    setSettings(updated);
    setNewYear('');
  };

  if (loading) {
    return <Container sx={{ mt: 2 }}><Typography>Loading settings...</Typography></Container>;
  }

  if (error && !settings) {
    return (
      <Container sx={{ mt: 2 }}>
        <Alert severity="error">{error} <Button onClick={fetchSettings}>Try Again</Button></Alert>
      </Container>
    );
  }

  if (!settings || !settings.discount || !settings.emailNotifications || !settings.emailNotifications.newRegistration) {
    return <Container sx={{ mt: 2 }}><Typography>Loading settings data...</Typography></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h5">OCIA Settings</Typography>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/ocia"
            startIcon={<ArrowBackIcon />}
          >
            Back to Registrations
          </Button>
        </Stack>

        {/* Form Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Public Form Header</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize the title and blurb shown at the top of the public registration form. Leave blank to use the defaults.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Form Title"
                placeholder="OCIA Registration"
                value={settings.formTitle || ''}
                onChange={(e) => handleSettingsChange('formTitle', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Form Subtitle / Blurb"
                placeholder="Order of Christian Initiation of Adults — St. Bridget University Parish"
                value={settings.formSubtitle || ''}
                onChange={(e) => handleSettingsChange('formSubtitle', e.target.value)}
                multiline
                minRows={1}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Year Management */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Year Management</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Current Year"
                value={settings.currentSchoolYear || ''}
                onChange={(e) => handleSettingsChange('currentSchoolYear', e.target.value)}
                SelectProps={{ native: true }}
                helperText="New registrations will be assigned to this year"
              >
                <option value="">Select a year</option>
                {Array.from(new Set([
                  ...(settings.currentSchoolYear ? [settings.currentSchoolYear] : []),
                  ...(settings.availableSchoolYears || [])
                ])).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  label="Add Year (e.g. 2027-2028)"
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddYear(); }}
                />
                <Button variant="outlined" onClick={handleAddYear} sx={{ whiteSpace: 'nowrap' }}>
                  Add Year
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Registration Status */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Registration Status</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.registrationEnabled}
                onChange={(e) => handleSettingsChange('registrationEnabled', e.target.checked)}
              />
            }
            label="Enable Registrations"
          />
        </Box>

        {/* Fee */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Registration Fee</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Registration Fee ($)"
                type="number"
                value={settings.registrationFee || 0}
                onChange={(e) => handleSettingsChange('registrationFee', Number(e.target.value))}
                InputProps={{ inputProps: { min: 0 } }}
                helperText="Set to 0 if OCIA has no fee"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Discount */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Early Discount</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.discount.enabled}
                    onChange={(e) => handleSettingsChange('discount.enabled', e.target.checked)}
                  />
                }
                label="Enable Discount"
              />
            </Grid>
            {settings.discount.enabled && (
              <>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Discount Valid Until Date"
                      value={settings.discount.endDate ? new Date(settings.discount.endDate) : null}
                      onChange={(date) => handleSettingsChange('discount.endDate', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Discounted Registration Fee ($)"
                    type="number"
                    value={settings.discount.discountedRegistrationFee || 0}
                    onChange={(e) => handleSettingsChange('discount.discountedRegistrationFee', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Decision deadline */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Decision Deadline</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shown on the registration form in the statement "I will attend the classes and make a decision by [date]."
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Decision By Date"
                  value={settings.decisionDeadlineDate ? new Date(settings.decisionDeadlineDate) : null}
                  onChange={(date) => handleSettingsChange('decisionDeadlineDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>

        {/* Email Notifications */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Email Notifications (New Registration)</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddRecipientDialog(true)}
            sx={{ mb: 2 }}
          >
            Add Recipient
          </Button>
          <List>
            {(settings.emailNotifications.newRegistration.recipients || []).map((recipient) => (
              <ListItem key={recipient}>
                <ListItemText primary={recipient} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveRecipient(recipient)}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Actions */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee' }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleCancelChanges}
              disabled={!hasChanges || isSaving}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isSaving}
              startIcon={<SaveIcon />}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Dialog open={addRecipientDialog} onClose={() => setAddRecipientDialog(false)}>
        <DialogTitle>Add Email Recipient</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={newRecipient}
            onChange={(e) => setNewRecipient(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRecipientDialog(false)}>Cancel</Button>
          <Button onClick={handleAddRecipient} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Container>
  );
};

export default OCIASettings;
